/**
 * adminApi.ts — TanStack Query hooks for the Super Admin client registry
 * (`/v1/admin/clients` on tender-api).
 *
 * A separate module from `tenderApi.ts` on purpose. Everything there is
 * tenant-scoped: it answers "what does this customer have". Everything here is
 * platform-scoped and gated on the `super_admin` role: it answers "which
 * customers exist". Mixing them would put a cross-tenant query key one
 * copy-paste away from a tenant surface.
 *
 * Every mutation here INVALIDATES rather than writing the response into the
 * cache. That is deliberate: a mutation whose `onSuccess` calls `setQueryData`
 * with its own response must be serialized, or two quick clicks apply the older
 * result last — the defect recorded in `feedback_cache_writing_mutations_race`.
 * Invalidate-only mutations cannot lose that race, and this surface is not
 * latency-sensitive enough to need the optimism.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, useApi } from "./api";

export type ClientStatus = "draft" | "provisioning" | "active" | "failed" | "suspended";

/** Italian display label per status, for the panel's pills. */
export const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
  draft: "Bozza",
  provisioning: "In corso…",
  active: "Attivo",
  failed: "Non riuscito",
  suspended: "Sospeso",
};

/** What each status MEANS, in a sentence. Shown as a tooltip, because "Bozza"
 *  does not tell a non-technical reader that nobody can log in yet. */
export const CLIENT_STATUS_HINT: Record<ClientStatus, string> = {
  draft: "Registrato qui, ma Auth0 non lo conosce ancora: nessuno può accedere.",
  provisioning: "Configurazione in corso su Auth0.",
  active: "Configurato: i suoi utenti possono accedere.",
  failed: "Un tentativo si è fermato a metà. Apri i dettagli per vedere dove.",
  // NOT "access withdrawn". Nothing in this system can withdraw Auth0 access yet:
  // there is no de-provision path and no endpoint can even set this status. Saying
  // otherwise was the exact lie the design review predicted. See the wiki page's
  // open items.
  suspended: "Escluso dal login. ATTENZIONE: l'accesso Auth0 esistente NON è ancora revocato.",
};

interface ProvisionStepDto {
  step: string;
  ok: boolean;
  detail: string | null;
}

interface ProvisionLogDto {
  at?: string;
  actor?: string;
  admin_email?: string;
  ok?: boolean;
  error?: string | null;
  steps?: ProvisionStepDto[];
}

interface ClientDto {
  id: string;
  slug: string;
  display_name: string;
  org_id: string | null;
  status: ClientStatus;
  kb_persona: string | null;
  kb_language: string | null;
  features: Record<string, boolean> | null;
  provisioned_at: string | null;
  provisioning_started_at: string | null;
  provision_log: ProvisionLogDto | null;
  created_by_sub: string;
  updated_by_sub: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminClient {
  id: string;
  slug: string;
  displayName: string;
  orgId: string | null;
  status: ClientStatus;
  kbPersona: string | null;
  kbLanguage: string | null;
  features: Record<string, boolean> | null;
  provisionedAt: string | null;
  provisionLog: ProvisionLogDto | null;
  createdAt: string;
}

function toClient(dto: ClientDto): AdminClient {
  return {
    id: dto.id,
    slug: dto.slug,
    displayName: dto.display_name,
    orgId: dto.org_id,
    status: dto.status,
    kbPersona: dto.kb_persona,
    kbLanguage: dto.kb_language,
    features: dto.features,
    provisionedAt: dto.provisioned_at,
    provisionLog: dto.provision_log,
    createdAt: dto.created_at,
  };
}

/** Never retry a 401/403: the global `retry: 1` would otherwise hit a
 *  permission-denied route twice for every render of a non-super-admin. */
function adminRetry(count: number, err: Error): boolean {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
  return count < 1;
}

const CLIENTS_KEY = ["admin", "clients"] as const;

export function useClients(enabled = true): UseQueryResult<AdminClient[], Error> {
  const api = useApi();
  return useQuery({
    queryKey: CLIENTS_KEY,
    enabled,
    retry: adminRetry,
    queryFn: async () => {
      const res = await api.cmeinsight.get<{ clients: ClientDto[] }>("/v1/admin/clients");
      return res.clients.map(toClient);
    },
  });
}

export interface ClientCreateInput {
  slug: string;
  displayName: string;
  kbPersona?: string | null;
  kbLanguage?: string | null;
}

export function useCreateClient(): UseMutationResult<AdminClient, Error, ClientCreateInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      toClient(
        await api.cmeinsight.post<ClientDto>("/v1/admin/clients", {
          body: {
            slug: input.slug,
            display_name: input.displayName,
            kb_persona: input.kbPersona ?? null,
            kb_language: input.kbLanguage ?? null,
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export interface ClientUpdateInput {
  clientId: string;
  displayName?: string;
  kbPersona?: string | null;
  kbLanguage?: string | null;
}

export function useUpdateClient(
  clientId: string,
): UseMutationResult<AdminClient, Error, ClientUpdateInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    /**
     * Serialized, unlike most invalidate-only mutations. Replacing a client's email
     * domains is DELETE-then-INSERT server-side, and two concurrent edits used to
     * MERGE rather than replace — both callers got a 200 describing a set the
     * database did not hold. The backend now takes a row lock, so this is the second
     * of the two guards: one fast double-click on the save button should produce one
     * request, not two that the database has to referee.
     */
    scope: { id: `update-client-${clientId}` },
    mutationFn: async ({ clientId, ...rest }) => {
      // Only the keys the caller actually set. The backend forbids unknown fields
      // AND distinguishes "omitted" from "null" (null clears the KB identity), so
      // sending undefined keys would either 422 or silently clear something.
      const body: Record<string, unknown> = {};
      if (rest.displayName !== undefined) body.display_name = rest.displayName;
      if (rest.kbPersona !== undefined) body.kb_persona = rest.kbPersona;
      if (rest.kbLanguage !== undefined) body.kb_language = rest.kbLanguage;
      return toClient(
        await api.cmeinsight.patch<ClientDto>(`/v1/admin/clients/${clientId}`, { body }),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export function useDeleteClient(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (clientId) => {
      await api.cmeinsight.del<void>(`/v1/admin/clients/${clientId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export interface ProvisionResult {
  client: AdminClient;
  steps: ProvisionStepDto[];
}

export function useProvisionClient(): UseMutationResult<
  ProvisionResult,
  Error,
  { clientId: string; adminEmail: string }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    /**
     * Serialized on the client id. Provisioning sends a real invitation email and
     * creates a real Auth0 Organization; the backend refuses a concurrent second
     * attempt with a 409, but there is no reason to make it do that work — and a
     * user who double-clicks should see one outcome, not a success racing a
     * conflict.
     */
    scope: { id: "provision-client" },
    mutationFn: async ({ clientId, adminEmail }) => {
      const res = await api.cmeinsight.post<{ client: ClientDto; steps: ProvisionStepDto[] }>(
        `/v1/admin/clients/${clientId}/provision`,
        {
          body: { admin_email: adminEmail },
          // Provisioning makes four to five Auth0 calls, each with its own retry
          // budget, so the default 30s deadline is too tight.
          timeoutMs: 90_000,
        },
      );
      return { client: toClient(res.client), steps: res.steps };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
    onError: () => {
      // A failed provision still WRITES to the row (status -> failed, plus the
      // provision_log showing where it stopped). Without this the panel would keep
      // showing "Bozza" after a failure and the log would be invisible.
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
    },
  });
}

export interface VerifyCheck {
  check: string;
  ok: boolean;
  detail: string | null;
}

export interface VerifyResult {
  clientId: string;
  slug: string;
  ok: boolean;
  checks: VerifyCheck[];
}

/** Italian label per backend check id. Unknown ids fall through to the raw id
 *  rather than being hidden — a check the UI has not been taught about is still
 *  information, and silently dropping it would make the checklist lie. */
export const VERIFY_CHECK_LABEL: Record<string, string> = {
  organization: "Organizzazione Auth0",
  org_id_matches_registry: "Corrispondenza con il registro",
  tenant_role_exists: "Ruolo utente esiste su Auth0",
  login_connection_enabled: "Metodo di accesso abilitato",
  membership_flags: "Iscrizione automatica al primo accesso",
  pending_invitations: "Inviti in attesa",
  registry_status: "Stato nel registro",
  auth0: "Collegamento ad Auth0",
};

export function useVerifyClient(): UseMutationResult<VerifyResult, Error, string> {
  const api = useApi();
  return useMutation({
    scope: { id: "verify-client" },
    mutationFn: async (clientId) => {
      const res = await api.cmeinsight.post<{
        client_id: string;
        slug: string;
        ok: boolean;
        checks: VerifyCheck[];
      }>(`/v1/admin/clients/${clientId}/verify`, { body: {}, timeoutMs: 60_000 });
      return { clientId: res.client_id, slug: res.slug, ok: res.ok, checks: res.checks };
    },
  });
}

// ── the customer's KB assistant identity ────────────────────────────────────
//
// Lives in the OTHER backend. `clients.kb_persona` in tender-api is the registry's
// record of what the vendor chose; `tenant_settings` in wiki-llm-service is what
// the KB chat actually reads. One panel field therefore writes to two services —
// the price of keeping the KB a standalone, industry-agnostic product, and the
// right trade.
//
// The KB write is only possible once the client has an `org_id`, i.e. after
// provisioning: the KB addresses tenants by Auth0 Organization and has never heard
// of a "client". The panel must not offer it before then, and `useSaveKbIdentity`
// refuses rather than silently skipping the half that matters.

export interface KbIdentity {
  persona: string | null;
  language: string | null;
  /** What the KB will actually use when `persona` is null. Read from the service
   *  rather than duplicated here, so the panel cannot show a stale default. */
  effectivePersona: string;
  isConfigured: boolean;
}

interface KbIdentityDto {
  assistant_persona: string | null;
  assistant_language: string | null;
  effective_persona: string;
  is_configured: boolean;
}

function toKbIdentity(dto: KbIdentityDto): KbIdentity {
  return {
    persona: dto.assistant_persona,
    language: dto.assistant_language,
    effectivePersona: dto.effective_persona,
    isConfigured: dto.is_configured,
  };
}

export function useKbIdentity(orgId: string | null): UseQueryResult<KbIdentity, Error> {
  const api = useApi();
  return useQuery({
    queryKey: ["admin", "kb-identity", orgId],
    enabled: Boolean(orgId),
    retry: adminRetry,
    queryFn: async () =>
      toKbIdentity(await api.wikiLLM.get<KbIdentityDto>(`/v1/platform/tenants/${orgId}/settings`)),
  });
}

/**
 * Saves the KB identity to BOTH services: the registry row (so the panel and any
 * future re-provision remember the choice) and the KB itself (so the assistant
 * actually changes).
 *
 * The KB write goes first. If it fails, the registry is left un-updated and the
 * mutation reports the error — which is the honest ordering: a registry that says
 * "persona set" while the assistant still uses the default is the state a support
 * conversation cannot recover from, whereas the reverse is visibly unsaved and the
 * operator simply presses save again.
 */
export function useSaveKbIdentity(): UseMutationResult<
  void,
  Error,
  { clientId: string; orgId: string | null; persona: string | null; language: string | null }
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    scope: { id: "save-kb-identity" },
    mutationFn: async ({ clientId, orgId, persona, language }) => {
      if (!orgId) {
        throw new ApiError(
          409,
          "Configura prima l'accesso del cliente: l'identità dell'assistente si applica " +
            "all'organizzazione, che non esiste ancora.",
        );
      }
      await api.wikiLLM.put<KbIdentityDto>(`/v1/platform/tenants/${orgId}/settings`, {
        body: { assistant_persona: persona, assistant_language: language },
      });
      await api.cmeinsight.patch<ClientDto>(`/v1/admin/clients/${clientId}`, {
        body: { kb_persona: persona, kb_language: language },
      });
    },
    onSuccess: (_data, { orgId }) => {
      void qc.invalidateQueries({ queryKey: CLIENTS_KEY });
      void qc.invalidateQueries({ queryKey: ["admin", "kb-identity", orgId] });
    },
  });
}
